import {
  AtSign,
  BadgeCheck,
  Briefcase,
  Camera,
  Cloud,
  Gamepad2,
  Ghost,
  Hash,
  MessageCircle,
  MessageSquare,
  Music,
  Pin,
  Play,
  Send,
  Share2,
  Star,
  ThumbsUp,
  X as XIcon,
} from "lucide-react";

// A lucide tirou os ícones de marca (Twitter, Youtube e afins) — o que sobra
// é escolher um símbolo genérico que lembre cada rede. Importar o nome antigo
// quebra o build, não o runtime, então isto não passa despercebido.

// As redes que chegam pela Zernio. O canal da conexão vem como
// "zernio:<rede>": um valor por rede fixado aqui obrigaria a mexer no código
// a cada rede nova que a Zernio passar a suportar, então o prefixo é que
// identifica a origem e o sufixo escolhe a aparência.
const REDES_ZERNIO = {
  instagram: {
    rotulo: "Instagram",
    icone: AtSign,
    classe: "bg-pink-500 text-white",
    classePill: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
    corSelo: "#E1306C",
  },
  facebook: {
    rotulo: "Messenger",
    icone: ThumbsUp,
    classe: "bg-sky-500 text-white",
    classePill: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
    corSelo: "#4267B2",
  },
  telegram: {
    rotulo: "Telegram",
    icone: Send,
    classe: "bg-cyan-500 text-white",
    classePill: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    corSelo: "#229ED9",
  },
  twitter: {
    rotulo: "X",
    icone: XIcon,
    classe: "bg-neutral-800 text-white",
    classePill: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
    corSelo: "#111111",
  },
  x: {
    rotulo: "X",
    icone: XIcon,
    classe: "bg-neutral-800 text-white",
    classePill: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
    corSelo: "#111111",
  },
  discord: {
    rotulo: "Discord",
    icone: Gamepad2,
    classe: "bg-indigo-500 text-white",
    classePill: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    corSelo: "#5865F2",
  },
  slack: {
    rotulo: "Slack",
    icone: Hash,
    classe: "bg-fuchsia-600 text-white",
    classePill: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-400",
    corSelo: "#4A154B",
  },
  threads: {
    rotulo: "Threads",
    icone: MessageSquare,
    classe: "bg-neutral-700 text-white",
    classePill: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
    corSelo: "#000000",
  },
  tiktok: {
    rotulo: "TikTok",
    icone: Music,
    classe: "bg-rose-600 text-white",
    classePill: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    corSelo: "#FE2C55",
  },
  pinterest: {
    rotulo: "Pinterest",
    icone: Pin,
    classe: "bg-red-700 text-white",
    classePill: "bg-red-500/10 text-red-700 dark:text-red-400",
    corSelo: "#BD081C",
  },
  snapchat: {
    rotulo: "Snapchat",
    icone: Camera,
    classe: "bg-yellow-400 text-black",
    classePill: "bg-yellow-400/10 text-yellow-600 dark:text-yellow-400",
    corSelo: "#FFFC00",
  },
  reddit: {
    rotulo: "Reddit",
    icone: Ghost,
    classe: "bg-orange-500 text-white",
    classePill: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    corSelo: "#FF4500",
  },
  bluesky: {
    rotulo: "Bluesky",
    icone: Cloud,
    classe: "bg-blue-500 text-white",
    classePill: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    corSelo: "#0285FF",
  },
  whatsapp: {
    rotulo: "WhatsApp",
    icone: MessageCircle,
    classe: "bg-emerald-500 text-white",
    classePill: "bg-primary/10 text-primary",
    corSelo: "#25D366",
  },
  linkedin: {
    rotulo: "LinkedIn",
    icone: Briefcase,
    classe: "bg-sky-700 text-white",
    classePill: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
    corSelo: "#0A66C2",
  },
  youtube: {
    rotulo: "YouTube",
    icone: Play,
    classe: "bg-red-600 text-white",
    classePill: "bg-red-500/10 text-red-600 dark:text-red-400",
    corSelo: "#FF0000",
  },
  googlebusiness: {
    rotulo: "Google Meu Negócio",
    icone: Star,
    classe: "bg-amber-500 text-white",
    classePill: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    corSelo: "#F4B400",
  },
};

// A identidade de uma rede: nome, glifo e cor. Fonte única — a tela da Zernio
// desenha as contas com ela e a lista de tickets marca a conversa com ela, e
// as duas precisam concordar sobre o que é "Messenger" e de que cor ele é.
//
// Rede que a Zernio passou a suportar e ainda não está no mapa não pode sair
// em branco nem virar "WhatsApp": devolve o próprio nome, em cinza.
export const identidadeDaRede = (plataforma) => {
  const rede = String(plataforma || "");
  const conhecida = REDES_ZERNIO[rede];

  return {
    chave: `zernio-${rede}`,
    rotulo: conhecida?.rotulo || rede.charAt(0).toUpperCase() + rede.slice(1),
    icone: conhecida?.icone || Share2,
    classe: conhecida?.classe || "bg-slate-500 text-white",
    classePill:
      conhecida?.classePill ||
      "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    corSelo: conhecida?.corSelo || "#64748B",
  };
};

// De onde a conversa veio. O que separa o WhatsApp oficial do não oficial é
// ter uma integração da Meta por trás: com ela é Cloud API, sem ela é a
// sessão do celular.
export const canalDoTicket = (ticket) => {
  const conexao = ticket?.whatsapp;
  const canal = conexao?.channel || "whatsapp";

  if (canal.startsWith("zernio:")) {
    return identidadeDaRede(canal.slice("zernio:".length));
  }

  if (canal === "facebook") {
    return {
      chave: "facebook",
      rotulo: "Facebook",
      icone: ThumbsUp,
      classe: "bg-sky-500 text-white",
      classePill: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
      corSelo: "#4267B2",
    };
  }

  if (canal === "instagram") {
    return {
      chave: "instagram",
      rotulo: "Instagram",
      icone: AtSign,
      classe: "bg-pink-500 text-white",
      classePill: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
      corSelo: "#E1306C",
    };
  }

  // Bot da Bot API do Telegram (Fase 4). Reusa a identidade que a Zernio já
  // desenha para a mesma rede: um Telegram é um Telegram, venha por qual
  // caminho vier — duas cores para a mesma rede confundiriam quem atende.
  if (canal === "telegram") {
    return { ...identidadeDaRede("telegram"), chave: "telegram" };
  }

  // Chat no navegador (link público, widget, link por atendimento).
  if (canal === "webchat") {
    return {
      chave: "webchat",
      rotulo: "Webchat",
      icone: MessageSquare,
      classe: "bg-indigo-500 text-white",
      classePill: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
      corSelo: "#6366F1",
    };
  }

  if (canal === "whatsapp_official" || conexao?.metaIntegrationId) {
    return {
      chave: "whatsapp-oficial",
      rotulo: "WhatsApp API Oficial",
      icone: BadgeCheck,
      classe: "bg-emerald-600 text-white",
      classePill: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      corSelo: "#128C7E",
    };
  }

  return {
    chave: "whatsapp",
    rotulo: "WhatsApp",
    icone: MessageCircle,
    classe: "bg-emerald-500 text-white",
    classePill: "bg-primary/10 text-primary",
    corSelo: "#25D366",
  };
};

export default canalDoTicket;
